import React from 'react';
import {
  MapPinIcon,
  TruckIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  NavigationIcon,
  ClockIcon,
  GlobeAltIcon,
  LocationMarkerIcon,
  PaperAirplaneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
    MapIcon,
} from '@heroicons/react/24/outline';

export default function TravelHelp() {
  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 rounded-full shadow-lg">
              <MapPinIcon className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            GPS Travel Management
          </h1>
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-full inline-block mb-6">
            <span className="text-xl font-semibold">Real-Time Field Service Tracking</span>
          </div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Experience cutting-edge field service management with GPS-powered travel tracking,
            automated status updates, and seamless Google Maps integration for optimal route planning
            and workforce mobility management.
          </p>
        </div>

        {/* Innovation Highlights */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 mb-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Why GPS Travel Management is Revolutionary</h2>
            <p className="text-cyan-100 text-lg">Transforming field service operations with location intelligence</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Real-Time GPS Tracking</h3>
              <p className="text-cyan-100">Automated location updates throughout the entire travel journey with precise geolocation data</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Route Optimization</h3>
              <p className="text-cyan-100">One-click Google Maps integration with optimized directions from company base to client locations</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Status Updates</h3>
              <p className="text-cyan-100">Intelligent travel sequence tracking with automatic status progression and time logging</p>
            </div>
          </div>
        </div>

        {/* Core System Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <GlobeAltIcon className="h-6 w-6 text-cyan-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">The Complete Travel Management Ecosystem</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Daily Planning */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <ClockIcon className="h-6 w-6 text-cyan-600 mr-3" />
                <h3 className="text-xl font-semibold text-cyan-800">Daily Travel Planning</h3>
              </div>
              <p className="text-cyan-700 mb-4">Comprehensive daily itinerary with all client meetings and appointments</p>
              <ul className="space-y-2 text-cyan-600">
                <li>• Date navigation with calendar controls</li>
                <li>• Complete meeting schedules</li>
                <li>• Client and attendee information</li>
                <li>• Travel time and distance estimates</li>
              </ul>
            </div>

            {/* GPS Tracking */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <MapIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-blue-800">GPS Location Tracking</h3>
              </div>
              <p className="text-blue-700 mb-4">Real-time geolocation updates throughout the entire field service journey</p>
              <ul className="space-y-2 text-blue-600">
                <li>• Automatic location capture</li>
                <li>• Journey phase tracking</li>
                <li>• Backend data synchronization</li>
                <li>• Location accuracy validation</li>
              </ul>
            </div>

            {/* Maps Integration */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-l-4 border-indigo-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <MapPinIcon className="h-6 w-6 text-indigo-600 mr-3" />
                <h3 className="text-xl font-semibold text-indigo-800">Google Maps Integration</h3>
              </div>
              <p className="text-indigo-700 mb-4">Seamless navigation with optimized routes and real-time traffic updates</p>
              <ul className="space-y-2 text-indigo-600">
                <li>• One-click directions</li>
                <li>• Company address as starting point</li>
                <li>• Client location targeting</li>
                <li>• Mobile-optimized interface</li>
              </ul>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">How the GPS Travel System Works</h2>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Daily Travel Planning</h3>
                <p className="text-gray-700 mb-3">
                  Start your day by reviewing your complete travel itinerary. Navigate between dates to see
                  all scheduled client meetings, including times, locations, attendees, and travel details.
                </p>
                <div className="bg-cyan-50 border-l-4 border-cyan-500 p-4 rounded">
                  <p className="text-cyan-800 font-medium">GPS Innovation:</p>
                  <p className="text-cyan-700">Unlike traditional scheduling apps, this system integrates real-time GPS tracking with your daily calendar.</p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">GPS Route Navigation</h3>
                <p className="text-gray-700 mb-3">
                  Get instant directions to your first client meeting. The system automatically uses your
                  company address as the starting point and provides optimized routes with real-time traffic updates.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-blue-800 font-medium">GPS Innovation:</p>
                  <p className="text-blue-700">One-click access to Google Maps with pre-configured routes from your office to client locations.</p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Automated Travel Tracking</h3>
                <p className="text-gray-700 mb-3">
                  Track your entire journey with GPS-powered status updates. Mark when you depart,
                  arrive at the client, and begin your return journey - all with automatic location logging.
                </p>
                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded">
                  <p className="text-indigo-800 font-medium">GPS Innovation:</p>
                  <p className="text-indigo-700">Real-time GPS coordinates are automatically captured and sent to the backend for precise travel logging.</p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Journey Analytics</h3>
                <p className="text-gray-700 mb-3">
                  Your complete travel data is logged and analyzed, providing insights into travel times,
                  distances, and efficiency for continuous improvement of field service operations.
                </p>
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                  <p className="text-purple-800 font-medium">GPS Innovation:</p>
                  <p className="text-purple-700">Every GPS coordinate and status change creates a comprehensive audit trail for travel management and compliance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Travel Status Tracking */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ArrowPathIcon className="h-6 w-6 text-cyan-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Intelligent Status Tracking</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Departing:</strong> GPS location captured when leaving for client meeting with automatic timestamp</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Arrived:</strong> Location verified upon reaching client destination with arrival confirmation</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Returning:</strong> GPS tracking continues during return journey with completion logging</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Sequence Control:</strong> Intelligent button progression prevents out-of-order status updates</span>
              </li>
            </ul>
          </div>

          {/* Meeting Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <PaperAirplaneIcon className="h-6 w-6 text-cyan-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Comprehensive Meeting Data</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Client Details:</strong> Complete client information with contact details and account history</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Attendee Lists:</strong> All meeting participants with roles and contact information</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Time Management:</strong> Scheduled meeting times with travel time estimates and buffers</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Distance Tracking:</strong> Round-trip distance calculations for mileage and expense tracking</span>
              </li>
            </ul>
          </div>

          {/* Mobile Optimization */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DevicePhoneMobileIcon className="h-6 w-6 text-cyan-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Mobile-First Design</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Responsive Interface:</strong> Optimized for mobile devices with touch-friendly controls</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>GPS Integration:</strong> Native mobile GPS access for accurate location tracking</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Offline Capability:</strong> Core functionality works without internet for critical operations</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Quick Actions:</strong> One-tap buttons for common actions like navigation and status updates</span>
              </li>
            </ul>
          </div>

          {/* Route Optimization */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <MapPinIcon className="h-6 w-6 text-cyan-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Smart Route Management</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Google Maps Integration:</strong> Direct integration with Google Maps for optimal routing</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Company Base Starting Point:</strong> All routes automatically start from company headquarters</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-Time Traffic:</strong> Live traffic data integration for accurate ETA calculations</span>
              </li>
              <li className="flex items-start">
                <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Alternative Routes:</strong> Multiple route options with time and distance comparisons</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Innovation Impact */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 mb-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">The Impact of GPS Travel Management</h2>
            <p className="text-indigo-100 text-lg">Revolutionizing field service operations</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">60%</div>
              <p className="text-indigo-100">Reduction in travel time</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">95%</div>
              <p className="text-indigo-100">Accurate location tracking</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">40%</div>
              <p className="text-indigo-100">Improvement in time management</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">80%</div>
              <p className="text-indigo-100">Reduction in administrative overhead</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">GPS Travel Management Best Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Enable GPS permissions for accurate tracking</li>
                <li>• Keep device charged for full-day operations</li>
                <li>• Use mobile data for real-time updates</li>
                <li>• Test GPS accuracy in different environments</li>
                <li>• Close unnecessary apps to preserve battery</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🚗 Travel Efficiency</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Plan routes the night before</li>
                <li>• Allow buffer time for traffic delays</li>
                <li>• Update status immediately upon arrival/departure</li>
                <li>• Use Google Maps for real-time navigation</li>
                <li>• Monitor travel times for route optimization</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Data Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Regularly sync location data</li>
                <li>• Review travel logs for accuracy</li>
                <li>• Report any GPS tracking issues</li>
                <li>• Maintain accurate client addresses</li>
                <li>• Update contact information promptly</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⏰ Time Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Arrive 10 minutes early for meetings</li>
                <li>• Account for travel time in scheduling</li>
                <li>• Update status changes immediately</li>
                <li>• Plan return journeys with traffic in mind</li>
                <li>• Allow flexibility for unexpected delays</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔧 Technical Maintenance</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Keep mobile OS updated</li>
                <li>• Clear cache regularly for better performance</li>
                <li>• Test GPS functionality weekly</li>
                <li>• Report technical issues immediately</li>
                <li>• Backup important travel data</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📈 Performance Tracking</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Monitor average travel times</li>
                <li>• Track on-time arrival rates</li>
                <li>• Analyze route efficiency</li>
                <li>• Review GPS data accuracy</li>
                <li>• Optimize schedules based on data</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}